import { ArrowDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const STAGES = [
  { actor: "Customer searches", note: "They need a job done and reach for their phone. If your business isn't visible, you're not in the running." },
  { actor: "Finds your business", note: "A complete Google presence with clear services, hours and one-tap calling helps customers choose you with confidence." },
  { actor: "Visits your website", note: "A fast, professional site builds trust in seconds. An outdated or slow one sends them straight back." },
  { actor: "Makes an enquiry", note: "Simple quote forms, click-to-call and WhatsApp make getting in touch effortless — day or night." },
  { actor: "Receives a response", note: "Automated acknowledgements mean every enquiry gets an instant, professional reply — even while you're on a job." },
  { actor: "Becomes a customer", note: "Quick, professional responses make it easier to turn enquiries into customers. Details are captured, followed up, and ready when you are." },
];

export default function Journey() {
  return (
    <section id="journey" data-testid="journey-section" className="py-20 sm:py-28 bg-navy-900/40 border-y border-sky-500/10">
      <div className="section-shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <p className="micro-label">The Problem</p>
              <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
                Stop Losing Customers Online
              </h2>
              <p className="mt-5 text-sm sm:text-base leading-relaxed text-slate-400">
                Many good local businesses lose potential customers every week — because their website is
                outdated, they're difficult to find on Google, enquiries get missed, or customers simply
                don't receive a quick enough response.
              </p>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">
                We fix the whole journey, end to end. Here's the path every local customer takes — and
                where we make sure you win them:
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <figure className="mt-8 relative rounded-2xl overflow-hidden border border-sky-500/15 vignette">
                <img
                  src="https://images.unsplash.com/photo-1790750541166-c799609feed3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBjb2ZmZWUlMjBzaG9wJTIwc21pbGluZ3xlbnwwfHx8fDE3OTA3OTc1ODB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Independent local business owner welcoming customers into their shop"
                  className="w-full h-56 sm:h-64 object-cover"
                  loading="lazy"
                />
                <figcaption className="absolute bottom-3 left-3 glass-panel rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300">
                  Your customers are local
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          <ol className="relative">
            {STAGES.map((s, i) => (
              <Reveal key={s.actor} delay={0.05 * i}>
                <li data-testid={`journey-stage-${i + 1}`} className="relative flex gap-5 sm:gap-7 pb-10 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span className="flex items-center justify-center w-11 h-11 rounded-full border border-sky-500/30 bg-navy-950 font-mono text-xs font-semibold text-sky-400 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {i < STAGES.length - 1 && <span className="flex-1 w-px bg-gradient-to-b from-sky-500/40 to-sky-500/5 mt-2" />}
                  </div>
                  <div className="pb-2">
                    <h3 className="font-heading text-lg sm:text-xl font-bold text-white tracking-tight">{s.actor}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400 max-w-lg">{s.note}</p>
                    {i < STAGES.length - 1 && <ArrowDown size={14} className="mt-4 text-sky-500/50" />}
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
